import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import Button from "@mui/material/Button";
import TextInput from "../ui/TextInput";
import React from "react";
import { API_HOST } from "../utils";
import { REVIEW_FILTERS } from "../api/artistReview";
import { describeApiError } from "../api/request";
import useArtistReview from "../hooks/useArtistReview";
import ArtistReviewCard from "../components/ArtistReviewCard";
function ReviewQueue({ session }) {
  const form = useForm({
    defaultValues: {
      searchInput: "",
    },
    mode: "onChange",
  });
  const review = useArtistReview();
  const [searchInput, setSearchInput] = useFormField(form, "searchInput");
  const [expanded, setExpanded] = React.useState(new Set());
  const region = React.useRef(null);
  const focused = React.useRef(null);
  const artists = review.data?.artists;
  const profileVersion = React.useRef(session.profileVersion);
  const refresh = review.refresh;
  React.useEffect(() => {
    if (profileVersion.current !== session.profileVersion) {
      profileVersion.current = session.profileVersion;
      refresh();
    }
  }, [session.profileVersion, refresh]);
  React.useEffect(() => {
    if (artists)
      setExpanded(
        (previous) =>
          new Set(
            [...previous].filter((id) =>
              artists.some((artist) => artist.id === id),
            ),
          ),
      );
  }, [artists]);
  React.useLayoutEffect(() => {
    // Restore focus only when a review action was removed, never when the user
    // has moved into another row or the name editor while a request was pending.
    if (
      focused.current &&
      !focused.current.element.isConnected &&
      document.activeElement === document.body
    ) {
      const target =
        focused.current.nextActions.find(
          (element) =>
            element.isConnected &&
            !element.disabled &&
            !element.closest("[hidden]"),
        ) ||
        (focused.current.header?.isConnected && focused.current.header) ||
        region.current?.querySelector("[data-card-disclosure]") ||
        region.current?.querySelector("[data-refresh]");
      target?.focus();
    }
  }, [artists]);
  if (review.denied)
    return (
      <Alert role="alert" severity="error">
        Access denied.{" "}
        <Link href={`${API_HOST}/auth/discogs/login/`}>
          Log in with Discogs
        </Link>{" "}
        to refresh your session.
      </Alert>
    );
  const sessionUnavailable = session.refreshing || !!session.error;
  const busy = review.hasPending || sessionUnavailable;
  return (
    <section
      className="artist-review-page"
      ref={region}
      onFocusCapture={(event) => {
        const actions = [
          ...region.current.querySelectorAll("[data-review-action]"),
        ];
        const index = actions.indexOf(event.target);
        focused.current = {
          element: event.target,
          nextActions: index < 0 ? [] : actions.slice(index + 1),
          header: event.target
            .closest("article")
            ?.querySelector("[data-card-disclosure]"),
        };
      }}
    >
      <h1>Artist Name Review</h1>
      <p>
        Decisions apply to the shared artist for all users. Approval makes a
        name eligible for future lookups; it does not start enrichment.
      </p>
      <form
        className="review-search"
        role="search"
        aria-label="Artist name review"
        onSubmit={form.handleSubmit(() => {
          if (!busy) review.changeSearch(searchInput);
        })}
      >
        <label htmlFor="review-search">Search primary artist names</label>
        <div className="review-search-controls">
          <TextInput
            id="review-search"
            type="search"
            value={searchInput}
            disabled={busy}
            aria-describedby="review-search-help"
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Button className="btn" type="submit" disabled={busy}>
            Search
          </Button>
          <Button
            className="btn"
            type="button"
            disabled={busy || (!searchInput && !review.search)}
            onClick={() => {
              setSearchInput("");
              review.changeSearch("");
            }}
          >
            Clear
          </Button>
        </div>
        <p id="review-search-help">
          Matches primary artist names, ignoring case. Leave blank to show all
          names.
        </p>
      </form>
      <div className="review-toolbar">
        <Tabs
          aria-label="Variation status"
          value={review.filter}
          onChange={(_, filter) => review.changeFilter(filter)}
          selectionFollowsFocus
          variant="scrollable"
          scrollButtons="auto"
        >
          {REVIEW_FILTERS.map((filter) => (
            <Tab
              key={filter}
              id={`review-tab-${filter}`}
              aria-controls="review-results"
              disabled={busy}
              value={filter}
              label={filter[0].toUpperCase() + filter.slice(1)}
            />
          ))}
        </Tabs>
        <Button
          className="btn"
          data-refresh
          onClick={review.refresh}
          disabled={busy || review.loading}
        >
          Refresh
        </Button>
      </div>
      <p role="status" aria-live="polite">
        {session.refreshing ? "Checking session…" : review.announcement}
      </p>
      {session.error && (
        <Alert role="alert" severity="error">
          Could not refresh session: {session.error.message}
          <Button
            className="btn"
            onClick={session.refreshProfile}
            disabled={session.refreshing}
          >
            Retry session
          </Button>
        </Alert>
      )}
      {review.error && (
        <Alert role="alert" severity="error">
          {describeApiError(review.error)} Use Refresh to try again.
        </Alert>
      )}
      <div
        id="review-results"
        role="tabpanel"
        aria-labelledby={`review-tab-${review.filter}`}
        aria-busy={review.loading}
      >
        {review.loading && (
          <p role="status">
            {review.data ? "Refreshing review queue…" : "Loading review queue…"}
          </p>
        )}
        {!review.loading && !review.error && artists?.length === 0 && (
          <p>
            {review.search
              ? "No matching artists."
              : review.filter === "unreviewed"
                ? "No unreviewed variations."
                : "No matching variations."}
          </p>
        )}
        <div className="artist-review-grid">
          {artists?.map((artist) => (
            <ArtistReviewCard
              key={artist.id}
              artist={artist}
              expanded={expanded.has(artist.id)}
              disabled={sessionUnavailable}
              review={review}
              onToggle={() =>
                setExpanded((previous) => {
                  const next = new Set(previous);
                  if (next.has(artist.id)) next.delete(artist.id);
                  else next.add(artist.id);
                  return next;
                })
              }
            />
          ))}
        </div>
      </div>
      <nav className="review-pagination" aria-label="Artist review pages">
        <Button
          className="btn"
          disabled={busy || review.loading || review.page === 1}
          onClick={() => review.changePage(review.page - 1)}
        >
          Previous
        </Button>
        <span>
          Page {review.page}
          {review.data ? ` · ${review.data.total} matching artists` : ""}
        </span>
        <Button
          className="btn"
          disabled={
            busy ||
            review.loading ||
            !review.data ||
            review.page * review.data.page_size >= review.data.total
          }
          onClick={() => review.changePage(review.page + 1)}
        >
          Next
        </Button>
      </nav>
    </section>
  );
}
export default function ArtistNameReviewPage({ session }) {
  if (session.status === "loading")
    return <p role="status">Checking session…</p>;
  if (!session.username)
    return (
      <p>
        Log in to continue.{" "}
        <Link href={`${API_HOST}/auth/discogs/login/`}>
          Log in with Discogs
        </Link>
      </p>
    );
  if (session.role !== "admin")
    return (
      <Alert role="alert" severity="error">
        Access denied. Artist Name Review requires an admin.
      </Alert>
    );
  return (
    <ReviewQueue
      key={`${session.username}:${session.role}`}
      session={session}
    />
  );
}
